import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import type { BillingMe, PlanInfo } from '../lib/types'
import { Brand, ThemeToggle } from '../components/Bits'
import { Account } from '../components/Account'
import { useAuth } from '../lib/auth'
import { useSeo } from '../lib/seo'
import { GOAL, reachGoal } from '../lib/metrika'
import { demos, slides, when } from '../lib/plural'

const price = (kopecks: number) => `${(kopecks / 100).toLocaleString('ru-RU')} ₽`

/**
 * Витрина тарифов — публичная и индексируемая: это страница для тех, кто
 * прикидывает сервис, в том числе без входа. Свои тариф и история платежей
 * показываются здесь же, отдельного «личного кабинета» нет.
 */
export default function Pricing() {
  useSeo({ title: 'Тарифы — clapme', canonical: '/pricing', noindex: false })
  const { user, ready } = useAuth()
  const [plans, setPlans] = useState<PlanInfo[] | null>(null)
  const [mine, setMine] = useState<BillingMe | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [params] = useSearchParams()

  const reload = useCallback(() => {
    if (user) api.billing.me().then(setMine).catch(() => {})
  }, [user])

  useEffect(() => {
    api.billing.plans().then(setPlans).catch(() => setPlans([]))
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  // Возврат из ЮKassa: платёж проводит вебхук, не эта страница — поэтому не
  // «оплачено», а честное «проверяем».
  const justPaid = params.get('paid') === '1'

  const buy = async (plan: PlanInfo) => {
    // Цель на клик, а не на редирект: даже если ЮKassa не ответит, интерес к
    // покупке уже случился — воронка не должна терять этот шаг.
    reachGoal(GOAL.purchaseClick, { plan: plan.code })
    setBusy(plan.code)
    setError(null)
    try {
      const { confirmationUrl } = await api.billing.checkout(plan.code)
      window.location.href = confirmationUrl
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось начать оплату')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="wrap col" style={{ gap: 24, maxWidth: 1080 }}>
      <header className="row spread row-wrap">
        <Brand />
        <div className="row" style={{ gap: 10 }}>
          {user && (
            <Link className="account-login" to="/demos">
              Мои демонстрации
            </Link>
          )}
          <Account />
          <ThemeToggle />
        </div>
      </header>

      {justPaid && (
        <div className="card row spread row-wrap">
          <span>Оплата принята — тариф активируется в течение минуты, как только ЮKassa подтвердит платёж.</span>
          <button className="btn" onClick={reload}>
            Проверить
          </button>
        </div>
      )}

      <section className="col" style={{ gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: 32 }}>Тарифы</h1>
        <p className="muted" style={{ margin: 0 }}>
          Быстрый эфир без регистрации остаётся бесплатным и без лимитов тарифа. Тариф — про
          демонстрации: сколько хранить, сколько слайдов и какого размера зал запускать.
        </p>
        {mine && (
          <p className="muted" style={{ margin: 0 }}>
            Ваш тариф: <strong>{mine.plan.title}</strong>
            {mine.validUntil && ` до ${when(mine.validUntil)}`}
          </p>
        )}
      </section>

      <div className="row row-wrap" style={{ gap: 16, alignItems: 'stretch' }}>
        {plans === null && <p className="muted">Загружаем…</p>}
        {plans?.map((plan) => {
          const current = mine?.plan.code === plan.code
          const free = plan.priceKopecks === 0
          return (
            <div className="card col" style={{ gap: 10, flex: '1 1 260px' }} key={plan.code}>
              <div className="row spread">
                <h2 style={{ margin: 0, fontSize: 22 }}>{plan.title}</h2>
                {current && <span className="badge badge-live">ваш</span>}
              </div>
              <div>
                <strong style={{ fontSize: 28 }}>{free ? '0 ₽' : price(plan.priceKopecks)}</strong>
                <span className="muted tiny">
                  {free ? 'навсегда' : plan.periodDays === 30 ? ' / в месяц' : ` / ${plan.periodDays} дней`}
                </span>
              </div>
              <ul className="col" style={{ gap: 4, margin: 0, paddingLeft: 18 }}>
                <li>{demos(plan.limits.maxDecks)}</li>
                <li>
                  до {slides(plan.limits.maxSlides)} в демонстрации
                </li>
                <li>зал до {plan.limits.maxParticipants} человек</li>
              </ul>
              {!free && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => void buy(plan)}
                    disabled={busy !== null}
                    data-testid={`buy-${plan.code}`}
                  >
                    {busy === plan.code ? 'Создаём платёж…' : current ? 'Продлить' : 'Подключить'}
                  </button>
                  <span className="muted tiny">
                    Оплата — принятие{' '}
                    <Link to="/legal/agreement">пользовательского соглашения (оферты)</Link>
                  </span>
                </>
              )}
              {free && <span className="muted tiny">Действует автоматически после входа</span>}
            </div>
          )
        })}
      </div>

      {error && <div className="toast" role="status">{error}</div>}

      {ready && !user && (
        <p className="muted tiny" style={{ margin: 0 }}>
          Чтобы подключить тариф, сначала{' '}
          <a href="/api/auth/yandex" onClick={() => reachGoal(GOAL.loginClick)}>
            войдите через Яндекс
          </a>
          .
        </p>
      )}

      <footer className="home-footer">
        <span>clapme — интерактив для конференций, лекций, тренингов и корпоративных встреч.</span>
      </footer>
    </div>
  )
}
